import type { MiceImportPreview } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Dialog, DialogContent, DialogTitle, formatRawDate } from '@web/ui/core';

const FORMAT = {
  malha: 'mice.import.format.malha',
  needs_model: 'mice.import.format.needs_model',
  orders: 'mice.import.format.orders',
} as const;

/** A malha diz uma data e o projeto outra (a importação não sobrescreve): pede conferir premissas. */
function dateConflict({ eventDate, projectEventDate }: MiceImportPreview): string | null {
  if (!eventDate || !projectEventDate || eventDate === projectEventDate) return null;
  return t('mice.upload.eventDateConflict', {
    sheet: formatRawDate(eventDate, null),
    project: formatRawDate(projectEventDate, null),
  });
}

interface ImportPreviewDialogProps {
  /** `null` fecha o diálogo. */
  preview: MiceImportPreview | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** O que a planilha traz, antes de gravar: formato, contagens, data divergente e os problemas. */
export function ImportPreviewDialog({
  preview,
  busy,
  onCancel,
  onConfirm,
}: Readonly<ImportPreviewDialogProps>) {
  if (!preview) return null;
  const conflict = dateConflict(preview);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent>
        <DialogTitle>{t('mice.import.preview.title')}</DialogTitle>
        <p className="text-meta text-muted-foreground">{preview.fileName}</p>
        <p className="text-meta font-medium text-foreground">
          {t('mice.import.preview.summary', {
            format: t(FORMAT[preview.format]),
            valid: preview.valid,
            invalid: preview.invalid,
            duplicate: preview.duplicate,
          })}
        </p>
        {conflict ? <p className="text-meta text-tone-amber-ink">{conflict}</p> : null}
        {preview.problems.length > 0 ? (
          <ul className="flex max-h-60 flex-col gap-1 overflow-y-auto text-meta text-muted-foreground">
            {preview.problems.map((p) => (
              <li key={p.rowNumber}>
                {[t('mice.import.preview.row', { row: p.rowNumber }), p.email, p.message]
                  .filter(Boolean)
                  .join(' · ')}
              </li>
            ))}
          </ul>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            {t('mice.purchase.cancel')}
          </Button>
          <Button disabled={busy || preview.valid === 0} onClick={onConfirm}>
            {t('mice.import.preview.confirm', { count: preview.valid })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
